import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import NavBar from "./components/navBar/NavBar";
import Information from "./pages/Information";
import Contact from "./pages/Contact";
import Rules from "./pages/Rules";
import { Box, Container, CircularProgress, Skeleton, Typography } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import "./App.css";
import "leaflet/dist/leaflet.css";
import Checkout from "./components/checkout/Checkout";
import MainConcept from "./components/MainConcept";
import ReservationSummary from "./components/checkout/ReservationSummary";
import Cookies from "js-cookie";
import PaymentResult from "./components/web_pay/PaymentResult";

// ✅ NIVEL 2: Context + preload banner (sin “flash”)
import BannerContext from "./components/contexts/BannerContext";
import { obtenerImagenCabezal } from "./services/motorService";
import cabezalEnBlanco from "./components/banner/cabezalEnBlanco.jpg";

const serverRoute = "/MotorReservasAdmintour";
const BASE_URL = "/MotorReservasAdmintour"; // Cambia esto a '/QAMotorReservasAdmintour' para QA
// const BASE_URL = '/QAMotorReservasAdmintour'

const theme = createTheme({
  palette: {
    background: { default: "#F9F9F9" },
    primary: { light: "#008EAA", main: "#007289", dark: "#015869", contrastText: "#fff" },
    secondary: { light: "#008EAA", main: "#007289", dark: "#015869", contrastText: "#fff" },
  },
});

function AppLoadingScreen() {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Skeleton del banner */}
      <Skeleton variant="rectangular" sx={{ width: "100%", height: { xs: 140, sm: 200 } }} />

      <Box
        sx={{
          maxWidth: 900,
          mx: "auto",
          px: 2,
          mt: { xs: 4, sm: 6 },
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <CircularProgress size={26} />
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Cargando motor de reservas…
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Preparando el banner del hotel.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

function App() {
  const location = useLocation();

  const params = new URLSearchParams(location.search);

  // 🔎 contemplamos hotcod y hotCod (en tu app aparecen ambos)
  const hotcodParam = params.get("hotcod");
  const hotCodParamAlt = params.get("hotCod");
  const hotCod = hotcodParam ?? hotCodParamAlt ?? Cookies.get("hotCod") ?? null;

  const buildUrl = (path) => {
    const qs = new URLSearchParams(location.search);
  
    if (hotCod) {
      qs.set("hotcod", hotCod);
    }
  
    return `${BASE_URL}${path}${qs.toString() ? `?${qs.toString()}` : ""}`;
  };

  const [loading, setLoading] = useState(true);

  // ✅ Banner global precargado
  const [bannerUrl, setBannerUrl] = useState(null);
  const [bannerStatus, setBannerStatus] = useState("loading"); // loading | ready | fallback

  const navList = useMemo(
    () => [
      { title: "Disponibilidad", path: buildUrl("/disponibilidad") },
      { title: "Información", path: buildUrl("/informacion") },
      { title: "Contacto", path: buildUrl("/contacto") },
      { title: "Politicas de Reserva", path: buildUrl("/politicasDeReserva") },
    ],
    [location.search, hotCod]
  );

  // ✅ Mantener cookie hotCod (contemplando hotcod/hotCod)
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const hotCodeFromUrl = p.get("hotCod") ?? p.get("hotcod");

    if (hotCodeFromUrl) {
      Cookies.set("hotCod", hotCodeFromUrl, { expires: 1 });
    }

    setLoading(false);
  }, [location.search]);

  // ✅ Preload del banner (SIN mostrar banner por defecto primero)
  useEffect(() => {
    // cada vez que cambia hotel, volvemos a estado loading
    setBannerStatus("loading");
    setBannerUrl(null);

    if (!hotCod) {
      setBannerUrl(cabezalEnBlanco);
      setBannerStatus("fallback");
      return;
    }

    const cacheKey = `banner_${String(hotCod)}`;
    const cached = sessionStorage.getItem(cacheKey);

    if (cached) {
      setBannerUrl(cached);
      setBannerStatus("ready");
      return;
    }

    let cancelled = false;

    const preload = async () => {
      try {
        const imagenBase64 = await obtenerImagenCabezal(hotCod);

        let finalImg = cabezalEnBlanco;
        if (
          imagenBase64 &&
          typeof imagenBase64 === "string" &&
          !imagenBase64.startsWith("Imagen del Cabezal Sin Datos para el hotel")
        ) {
          finalImg = `data:image/png;base64,${imagenBase64}`;
        }

        // ✅ forzar precarga real de la imagen antes de "ready"
        const img = new Image();
        img.onload = () => {
          if (cancelled) return;
          sessionStorage.setItem(cacheKey, finalImg);
          setBannerUrl(finalImg);
          setBannerStatus("ready");
        };
        img.onerror = () => {
          if (cancelled) return;
          setBannerUrl(cabezalEnBlanco);
          setBannerStatus("fallback");
        };
        img.src = finalImg;
      } catch (e) {
        if (cancelled) return;
        setBannerUrl(cabezalEnBlanco);
        setBannerStatus("fallback");
      }
    };

    preload();

    return () => {
      cancelled = true;
    };
  }, [hotCod]);

  // ✅ CLAVE: NO renderizar el motor hasta que el banner esté listo (ready o fallback)
  if (loading || bannerStatus === "loading") {
    return (
      <ThemeProvider theme={theme}>
        <AppLoadingScreen />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <BannerContext.Provider
        value={{ bannerUrl, bannerStatus, setBannerUrl, setBannerStatus }}
      >
        <NavBar navList={navList} />
        <Container sx={{ mt: 0 }}>
          <Routes>
            <Route path={serverRoute} element={<Navigate to={buildUrl("/disponibilidad")} replace />} />
            <Route path={`${BASE_URL}/disponibilidad/*`} element={<MainConcept />} />
            <Route path={`${BASE_URL}/informacion`} element={<Information />} />
            <Route path={`${BASE_URL}/contacto`} element={<Contact />} />
            <Route path={`${BASE_URL}/politicasDeReserva`} element={<Rules />} />
            <Route path={`${BASE_URL}/checkout`} element={<Checkout />} />
            <Route path={`${BASE_URL}/payment/result`} element={<PaymentResult />} />
            <Route path={`${BASE_URL}/resumenReserva`} element={<ReservationSummary />} />
          </Routes>
        </Container>
      </BannerContext.Provider>
    </ThemeProvider>
  );
}

export default App;
